/**
 * Error handling utilities for the application
 * This file provides a standardized approach to error handling across the application.
 */

// Custom error types
export class AppError extends Error {
    constructor(message: string) {
        super(message);
        this.name = "AppError";
        // This is needed for proper instanceof checks in TypeScript
        Object.setPrototypeOf(this, AppError.prototype);
    }
}

export class NetworkError extends AppError {
    constructor(
        message: string,
        public originalError?: Error
    ) {
        super(message);
        this.name = "NetworkError";
        Object.setPrototypeOf(this, NetworkError.prototype);
    }
}

export class AuthError extends AppError {
    constructor(
        message: string,
        public originalError?: Error
    ) {
        super(message);
        this.name = "AuthError";
        Object.setPrototypeOf(this, AuthError.prototype);
    }
}

export class DataError extends AppError {
    constructor(
        message: string,
        public originalError?: Error
    ) {
        super(message);
        this.name = "DataError";
        Object.setPrototypeOf(this, DataError.prototype);
    }
}

export class ValidationError extends AppError {
    constructor(
        message: string,
        public field?: string
    ) {
        super(message);
        this.name = "ValidationError";
        Object.setPrototypeOf(this, ValidationError.prototype);
    }
}

// Error severity levels
export enum ErrorSeverity {
    ERROR = "error",
    CRITICAL = "critical",
}

// Error context interface
export interface ErrorContext {
    component?: string;
    action?: string;
    userId?: string;
    additionalData?: Record<string, unknown>;
}

// Error logging service
export class ErrorLogger {
    private static instance: ErrorLogger;

    private constructor() {
        // Private constructor to enforce singleton pattern
    }

    public static getInstance(): ErrorLogger {
        if (!ErrorLogger.instance) {
            ErrorLogger.instance = new ErrorLogger();
        }
        return ErrorLogger.instance;
    }

    /**
     * Log an error with context information
     * @param error The error to log
     * @param severity The severity level of the error
     * @param context Additional context information
     */
    public logError(error: Error, severity: ErrorSeverity = ErrorSeverity.ERROR, context?: ErrorContext): void {
        // In development, log to console
        if (process.env.NODE_ENV !== "production") {
            console.error(`[${severity.toUpperCase()}]`, error.message, {
                name: error.name,
                stack: error.stack,
                context,
            });
            return;
        }

        // In production, we could send to a logging service
        // This is where you would integrate with services like Sentry, LogRocket, etc.
        // For now, we'll just log to console in production as well
        console.error(`[${severity.toUpperCase()}]`, error.message, {
            name: error.name,
            context,
        });

        // Example of how to integrate with analytics
        try {
            // Import analytics only when needed to avoid circular dependencies
            import("./analytics")
                .then(({ trackEvent }) => {
                    trackEvent({
                        category: "error",
                        action: error.name,
                        label: `${error.message} (Severity: ${severity})`,
                        // Include additional context but not directly in the trackEvent properties
                    });
                })
                .catch((e) => {
                    // Silently fail if analytics is not available
                    console.error("Failed to import analytics module:", e);
                });
        } catch (e) {
            // Silently fail if analytics is not available
            console.error("Failed to track error event:", e);
        }
    }
}

// Get the singleton instance
export const errorLogger = ErrorLogger.getInstance();

/**
 * Utility function to wrap async functions with standardized error handling
 * @param fn The async function to wrap
 * @param errorHandler Optional custom error handler
 * @returns A wrapped function with error handling
 */
export function withErrorHandling<T, Args extends unknown[]>(
    fn: (...args: Args) => Promise<T>,
    errorHandler?: (error: Error) => void
): (...args: Args) => Promise<T> {
    return async (...args: Args): Promise<T> => {
        try {
            return await fn(...args);
        } catch (err) {
            // Convert to AppError if it's not already an instance of Error
            const error = err instanceof Error ? err : new AppError(String(err));

            // Log the error
            errorLogger.logError(error);

            // Call custom error handler if provided
            if (errorHandler) {
                errorHandler(error);
            }

            // Re-throw the error for the caller to handle if needed
            throw error;
        }
    };
}

/**
 * Helper function to create a network error from any error
 * @param err The original error
 * @param message Optional custom message
 * @returns A NetworkError instance
 */
export function createNetworkError(err: unknown, message?: string): NetworkError {
    const originalError = err instanceof Error ? err : undefined;
    const errorMessage = message || (originalError ? originalError.message : "Network request failed");
    return new NetworkError(errorMessage, originalError);
}

/**
 * Helper function to create a data error from any error
 * @param err The original error
 * @param message Optional custom message
 * @returns A DataError instance
 */
export function createDataError(err: unknown, message?: string): DataError {
    const originalError = err instanceof Error ? err : undefined;
    const errorMessage = message || (originalError ? originalError.message : "Data operation failed");
    return new DataError(errorMessage, originalError);
}

/**
 * Helper function to create an auth error from any error
 * @param err The original error
 * @param message Optional custom message
 * @returns An AuthError instance
 */
export function createAuthError(err: unknown, message?: string): AuthError {
    const originalError = err instanceof Error ? err : undefined;
    const errorMessage = message || (originalError ? originalError.message : "Authentication failed");
    return new AuthError(errorMessage, originalError);
}
